import * as React from 'react'
import {IntlShape, useIntl} from 'react-intl'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {ScoreRatingLarge} from '@vio/atlas/experimental/ScoreRatingLarge'
import {ScoreRating, ScoreRatingLoader} from '@vio/atlas/ScoreRating'
import {toggle} from 'opticks'

import {
  getReviewSentimentKey,
  reviewSentimentMessages
} from '@daedalus/core/src/hotel/reviewSentiments'
import {toOneDecimalOrInteger} from '@daedalus/core/src/utils/number'

export {ScoreRatingLoader as ReviewRatingLoader}

interface Props {
  rating: number
  totalRatingCount?: number
  withIcon?: boolean
  dataId?: string
  underlineSubLabel?: boolean
  hotelId?: string
  variant?: 'compact' | 'large' | 'default'
}

const DEFAULT_DATA_ID = 'ReviewRating'

const LabelWrapper = styled.span`
  &.isUnderline {
    text-decoration: underline;
  }
`

const getSubLabel = (
  totalRatingCount: Props['totalRatingCount'],
  variant: Props['variant'],
  formatMessage: IntlShape['formatMessage']
) => {
  switch (variant) {
    case 'large':
      if (!totalRatingCount) {
        return formatMessage({
          id: 'reviews.basedOnNoReviews',
          defaultMessage: 'Based on verified reviews'
        })
      }
      return formatMessage(
        {
          id: 'reviews.basedOnReviews',
          defaultMessage:
            '{reviewsCount, plural, one {Based on 1 verified review} other {Based on # verified reviews}}'
        },
        {reviewsCount: totalRatingCount}
      )

    case 'compact':
      if (!totalRatingCount) {
        return formatMessage({
          id: 'noReviews',
          defaultMessage: 'No reviews yet'
        })
      }
      return totalRatingCount?.toLocaleString()

    default:
      if (!totalRatingCount) {
        return formatMessage({
          id: 'noReviews',
          defaultMessage: 'No reviews yet'
        })
      }
      return formatMessage(
        {
          id: 'reviewsCount',
          defaultMessage: '{reviews, plural, one {# review} other {# reviews}}'
        },
        {reviews: totalRatingCount}
      )
  }
}

export const ReviewRating = ({
  rating,
  totalRatingCount,
  underlineSubLabel = false,
  dataId = DEFAULT_DATA_ID,
  variant = 'default',
  hotelId
}: Props) => {
  const {formatMessage} = useIntl()

  const sentimentMessageKey = getReviewSentimentKey(rating)
  const translatedSentiment = sentimentMessageKey
    ? reviewSentimentMessages[sentimentMessageKey]
    : null
  const isNoReviewsBSide = toggle('8783dc23-no-reviews', false, true)

  const formattedRating = toOneDecimalOrInteger(rating)

  if (formattedRating === null && !isNoReviewsBSide) {
    return null
  }

  const subLabel = getSubLabel(totalRatingCount, variant, formatMessage)

  if (variant === 'large') {
    return (
      <div data-id={dataId}>
        <ScoreRatingLarge
          value={formattedRating ?? 0}
          label={translatedSentiment && formatMessage(translatedSentiment)}
          subLabel={
            subLabel && (
              <LabelWrapper className={cx(underlineSubLabel && 'isUnderline')}>
                {subLabel}
              </LabelWrapper>
            )
          }
          scoreId={hotelId && `HotelCardMobileScoreValue-${hotelId}`}
          scoreLabelId={hotelId && `HotelCardMobileScoreLabel-${hotelId}`}
          scoreSubLabelId={hotelId && `HotelCardMobileScoreSubLabel-${hotelId}`}
        />
      </div>
    )
  }
  return (
    <div data-id={dataId}>
      <ScoreRating
        value={formattedRating ?? 0}
        label={translatedSentiment && formatMessage(translatedSentiment)}
        subLabel={
          subLabel && (
            <LabelWrapper className={cx(underlineSubLabel && 'isUnderline')}>
              {subLabel}
            </LabelWrapper>
          )
        }
        isCompact={variant === 'compact'}
        scoreId={hotelId && `HotelCardMobileScoreValue-${hotelId}`}
        scoreLabelId={hotelId && `HotelCardMobileScoreLabel-${hotelId}`}
        scoreSubLabelId={hotelId && `HotelCardMobileScoreSubLabel-${hotelId}`}
      />
    </div>
  )
}
